import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { config } from '../../config';
import Spinner from '../Global/Spinner';

const API_URL = config.API_URI;
// The hosted MCP endpoint AI clients connect to. Derived from the API origin so
// dev and prod both show the right URL.
const MCP_ENDPOINT = `${API_URL}/mcp`;

/**
 * "Connect to AI agents (MCP)" — lets a user connect their Zoobbe ACCOUNT to
 * Claude Desktop, Cursor, ChatGPT, etc. via the hosted MCP server. The
 * connection is user-based: it reaches every workspace the user belongs to
 * (not scoped to one workspace), which is why this lives in account settings.
 *
 * Two paths, matching the backend:
 *   - OAuth: paste the endpoint URL into a client that speaks MCP-over-OAuth;
 *     the client discovers + authorizes on its own. Nothing to copy here but
 *     the URL.
 *   - Pairing token: mint a token and paste it (with ready-made Claude/Cursor
 *     JSON). For clients that take a Bearer token directly.
 */
const McpConnectSection = () => {
  const dispatch = useDispatch();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [generated, setGenerated] = useState(null); // { token, endpoint, connect: {...} }
  const [copied, setCopied] = useState('');
  const [connectTab, setConnectTab] = useState('cli'); // cli | json | prompt | oauth

  const fetchTokens = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/v1/mcp/pairing`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTokens(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch MCP tokens:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/v1/mcp/pairing`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'AI agent' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create token');
      setGenerated(data.data);
      dispatch(showSnackBar({ message: 'Pairing token created', type: 'success' }));
      fetchTokens();
    } catch (err) {
      dispatch(showSnackBar({ message: err.message, type: 'error' }));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      const res = await fetch(`${API_URL}/v1/mcp/pairing/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        dispatch(showSnackBar({ message: 'Token revoked', type: 'success' }));
        fetchTokens();
      }
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to revoke token', type: 'error' }));
    }
  };

  const copy = (text, which) => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return <div className="setting-row"><Spinner /></div>;

  return (
    <div className="mcp-connect-section">
      <div className="setting-row">
        <div className="setting-label">
          <h2>Connect to AI agents</h2>
          <p>
            Connect your Zoobbe account to Claude, Cursor, or any AI client that
            speaks MCP. Works across all your workspaces — ask your boards
            questions and create cards from plain English, right inside your AI
            tool.
          </p>
        </div>

        <div className="mcp-connect-content">
          {/* Path 1 — OAuth. The recommended route: nothing secret to copy or
              store, and the user can revoke from the client side too. */}
          <div className="mcp-path mcp-path--primary">
            <div className="mcp-path-head">
              <h3 className="mcp-path-title">Connect with OAuth</h3>
              <span className="mcp-path-badge">Recommended</span>
            </div>
            <p className="mcp-path-desc">
              Paste this endpoint into Claude, Cursor, or any MCP client that
              supports OAuth — it will walk you through authorizing. There's no
              token to copy or keep safe.
            </p>
            <div className="mcp-endpoint-display">
              <code className="mcp-endpoint-code">{MCP_ENDPOINT}</code>
              <button
                className="mcp-copy-btn"
                onClick={() => copy(MCP_ENDPOINT, 'endpoint')}
              >
                {copied === 'endpoint' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Generated pairing token + all connection methods */}
          {generated && (
            <div className="mcp-generated">
              <div className="mcp-warning">
                Copy your connection details now — the token won't be shown again.
              </div>

              {/* The raw token, for reference */}
              <div className="mcp-token-display">
                <code className="mcp-token-code">{generated.token}</code>
                <button
                  className="mcp-copy-btn"
                  onClick={() => copy(generated.token, 'token')}
                >
                  {copied === 'token' ? 'Copied!' : 'Copy token'}
                </button>
              </div>

              {/* Method tabs. OAuth isn't listed — it's the path above, and it
                  needs no token, so showing it here would be contradictory. */}
              <div className="mcp-tabs">
                {[
                  { id: 'cli', label: 'Claude CLI' },
                  { id: 'json', label: 'JSON config' },
                  { id: 'prompt', label: 'AI prompt' },
                ].map(t => (
                  <button
                    key={t.id}
                    className={`mcp-tab ${connectTab === t.id ? 'active' : ''}`}
                    onClick={() => setConnectTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Tab bodies */}
              {connectTab === 'cli' && (
                <div className="mcp-method">
                  <p className="mcp-method-hint">
                    Run this in your terminal (Claude Code / Claude CLI):
                  </p>
                  <div className="mcp-snippet">
                    <pre><code>{generated.connect?.cli}</code></pre>
                    <button className="mcp-copy-btn small" onClick={() => copy(generated.connect?.cli, 'cli')}>
                      {copied === 'cli' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {connectTab === 'json' && (
                <div className="mcp-method">
                  <p className="mcp-method-hint">
                    Add to your client's config file (Claude Desktop
                    <code>claude_desktop_config.json</code>, Cursor
                    <code>~/.cursor/mcp.json</code>):
                  </p>
                  <div className="mcp-snippet">
                    <pre><code>{generated.connect?.json}</code></pre>
                    <button className="mcp-copy-btn small" onClick={() => copy(generated.connect?.json, 'json')}>
                      {copied === 'json' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {connectTab === 'prompt' && (
                <div className="mcp-method">
                  <p className="mcp-method-hint">
                    Paste this to any AI agent that can add its own MCP servers:
                  </p>
                  <div className="mcp-snippet">
                    <pre><code>{generated.connect?.prompt}</code></pre>
                    <button className="mcp-copy-btn small" onClick={() => copy(generated.connect?.prompt, 'prompt')}>
                      {copied === 'prompt' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              <button className="mcp-done-btn" onClick={() => setGenerated(null)}>
                Done
              </button>
            </div>
          )}

          {/* Path 2 — pairing token, for clients that take a Bearer token. */}
          {!generated && (
            <div className="mcp-path">
              <div className="mcp-path-head">
                <h3 className="mcp-path-title">Or use a pairing token</h3>
              </div>
              <p className="mcp-path-desc">
                For clients that take a token directly instead of running OAuth.
                The token is shown once — treat it like a password.
              </p>

              <button
                className="mcp-add-btn"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? 'Creating…' : '+ Create pairing token'}
              </button>

              {tokens.length > 0 ? (
                <div className="mcp-tokens-list">
                  {tokens.map(t => (
                    <div key={t._id} className="mcp-token-item">
                      <div className="mcp-token-info">
                        <div className="mcp-token-name">{t.name}</div>
                        <div className="mcp-token-meta">
                          <code className="mcp-token-prefix">{t.prefix}...</code>
                          <span className="mcp-token-separator">·</span>
                          <span>
                            {t.lastUsedAt
                              ? `Used ${new Date(t.lastUsedAt).toLocaleDateString()}`
                              : 'Never used'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="mcp-revoke-btn"
                        onClick={() => handleRevoke(t._id)}
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mcp-tokens-empty">
                  No pairing tokens yet.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default McpConnectSection;
